import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CartoonLoader from '../CartoonLoader';
import AppPageHeader from '../AppPageHeader';
import { useData } from '../DataContext';
import { formatDate, formatTime, comparableScheduleDateKey } from '../sheetDateFormat';
import { CALENDAR_MONTHS_ORDER } from '../calendarMonth';

const PROMO_FILTERS_CACHE_KEY = 'teacher_webapp_promo_filters_cache_v1';

const defaultPromoFilters = {
  teacherName: [],
  class: [],
  courseName: [],
  month: [],
  shootStatus: '',
  startDate: '',
  endDate: '',
};

function loadPromoFiltersFromStorage() {
  try {
    const raw = localStorage.getItem(PROMO_FILTERS_CACHE_KEY);
    if (!raw) return defaultPromoFilters;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return defaultPromoFilters;
    return {
      teacherName: Array.isArray(p.teacherName) ? p.teacherName : (p.teacherName ? [p.teacherName] : []),
      class: Array.isArray(p.class) ? p.class : (p.class ? [p.class] : []),
      courseName: Array.isArray(p.courseName) ? p.courseName : (p.courseName ? [p.courseName] : []),
      month: Array.isArray(p.month)
        ? p.month.filter(m => CALENDAR_MONTHS_ORDER.includes(m))
        : [],
      shootStatus:
        p.shootStatus != null && String(p.shootStatus).trim() !== ''
          ? String(p.shootStatus).trim()
          : '',
      startDate: typeof p.startDate === 'string' ? p.startDate : '',
      endDate: typeof p.endDate === 'string' ? p.endDate : '',
    };
  } catch (_) {
    return defaultPromoFilters;
  }
}

function convertGoogleDriveLink(url) {
  if (!url) return url;
  let fileId = null;
  let match = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (match) fileId = match[1];
  if (!fileId) {
    match = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (match) fileId = match[1];
  }
  if (fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }
  return url;
}

/** Extract YouTube video id from common URL shapes. */
function parseYouTubeVideoId(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const url = rawUrl.trim();
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id && /^[a-zA-Z0-9_-]{6,}$/.test(id) ? id : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      const v = u.searchParams.get('v');
      if (v && /^[a-zA-Z0-9_-]{6,}$/.test(v)) return v;
      const pathMatch = u.pathname.match(/\/(?:embed|shorts|live)\/([a-zA-Z0-9_-]{6,})/);
      if (pathMatch) return pathMatch[1];
    }
  } catch (_) {
    // fall through to regex
  }
  let m = url.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
  if (m) return m[1];
  m = url.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
  if (m) return m[1];
  return null;
}

function getYouTubeEmbedUrl(url) {
  const id = parseYouTubeVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

function isGoogleDriveUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /drive\.google\.com/.test(url);
}

const viewButtonClass =
  'inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200';

/** gviz may return numbers for some cells; coerce before string ops. */
function promoShootStatusStr(value) {
  if (value == null || value === '') return '';
  return String(value).trim();
}

/** RAW / YouTube: in-modal embed + View (new tab), matching thumbnail pattern. */
function PromoVideoEmbedSection({ label, url }) {
  if (!url) {
    return (
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
        </div>
        <p className="text-gray-500 text-sm">—</p>
      </div>
    );
  }

  const ytEmbed = getYouTubeEmbedUrl(url);
  const drivePreview = convertGoogleDriveLink(url);
  const isDriveEmbed = isGoogleDriveUrl(url) && drivePreview.includes('/preview');

  return (
    <div className="border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={viewButtonClass}
        >
          View
        </a>
      </div>
      {ytEmbed ? (
        <div className="aspect-video w-full max-w-full rounded-lg overflow-hidden border border-gray-300 shadow-md bg-black">
          <iframe
            title={label}
            src={ytEmbed}
            className="w-full h-full min-h-[200px]"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      ) : isDriveEmbed ? (
        <div className="flex justify-center">
          <iframe
            src={drivePreview}
            className="w-full max-w-full h-96 rounded-lg shadow-md border border-gray-300 bg-gray-900"
            title={label}
          />
        </div>
      ) : /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url.split('?')[0]) ? (
        <video
          src={url}
          controls
          playsInline
          className="w-full max-h-[28rem] rounded-lg shadow-md border border-gray-300 bg-black"
        />
      ) : (
        <p className="text-sm text-gray-500 mt-1">
          In-app preview is not available for this link. Use View to open it in a new tab.
        </p>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = promoShootStatusStr(status);
  if (!s) return null;
  const lower = s.toLowerCase();
  const isDone = lower === 'done';
  const isPending = lower === 'pending';

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isDone
          ? 'bg-green-100 text-green-800'
          : isPending
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {isDone && (
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {isPending && (
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      )}
      {s}
    </span>
  );
}

function MultiSelect({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const toggle = (option) => {
    if (value.includes(option)) {
      onChange(value.filter(v => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const allSelected = options.length > 0 && value.length === options.length;
  const toggleAll = () => {
    onChange(allSelected ? [] : [...options]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        <span className="truncate">
          {value.length === 0
            ? label
            : value.length === 1
            ? value[0]
            : `${value.length} selected`}
        </span>
        <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
          {options.length > 1 && (
            <button
              type="button"
              onClick={toggleAll}
              className="w-full text-left px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 border-b border-gray-100"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          )}
          {options.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => toggle(option)}
              className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${value.includes(option) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                {value.includes(option) && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </span>
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function displayOrDash(val) {
  if (val === undefined || val === null || val === '') return '—';
  return String(val);
}

function PromoDetailModal({ row, onClose }) {
  if (!row) return null;

  const fields = [
    ['Time', row.time, 'time'],
    ['Class', row.class],
    ['Month', row.month],
    ['Caption', row.caption],
    ['Subject', row.subject],
    ['Subject (বাংলা)', row.subjectBn],
    ['Chapter', row.chapter],
    ['পর্ব/Part', row.part],
    ['Topic', row.topic],
    ['Topic List', row.topicList],
    ['Teacher Name', row.teacherName],
    ['Course Name', row.courseName],
    ['Shoot Deadline', row.shootDeadlineRaw || row.shootDeadline, 'deadline'],
    ['Shoot Status', row.shootStatus],
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Promotional content details</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Thumbnail link</label>
              {row.thumbnailLink ? (
                <a
                  href={row.thumbnailLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={viewButtonClass}
                >
                  View
                </a>
              ) : null}
            </div>
            {row.thumbnailLink ? (
              <div className="flex justify-center">
                <iframe
                  src={convertGoogleDriveLink(row.thumbnailLink)}
                  className="w-full max-w-full h-96 rounded-lg shadow-md border border-gray-300"
                  title="Thumbnail preview"
                />
              </div>
            ) : (
              <p className="text-gray-500 text-sm">—</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((entry) => {
              const label = entry[0];
              const val = entry[1];
              const kind = entry[2];
              let text;
              if (kind === 'time') {
                text = formatTime(row.time) || 'TBD';
              } else if (kind === 'deadline') {
                const fd = formatDate(row.shootDeadlineRaw || row.shootDeadline);
                text = fd === 'N/A' ? '—' : fd;
              } else {
                text = displayOrDash(val);
              }
              const fullWidth = label === 'Caption' || label === 'Topic List' || label === 'Course Name';
              return (
                <div key={label} className={fullWidth ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  {label === 'Shoot Status' && row.shootStatus ? (
                    <StatusBadge status={row.shootStatus} />
                  ) : label === 'Shoot Status' ? (
                    <p className="text-gray-500 text-sm">—</p>
                  ) : (
                    <p className="text-gray-900 whitespace-pre-wrap break-words">{text}</p>
                  )}
                </div>
              );
            })}
          </div>

          <PromoVideoEmbedSection label="RAW video" url={row.rawVideoLink} />
          <PromoVideoEmbedSection label="YouTube" url={row.ytLink} />
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PromoSchedulePage() {
  const navigate = useNavigate();
  const { promoData, promoLoading, promoError, refetchPromo } = useData();

  const [filters, setFilters] = useState(loadPromoFiltersFromStorage);

  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(PROMO_FILTERS_CACHE_KEY, JSON.stringify(filters));
    } catch (_) {}
  }, [filters]);

  useEffect(() => {
    if (!promoData.length) return;
    setFilters(prev => {
      const teachers = [...new Set(promoData.map(r => r.teacherName).filter(Boolean))];
      const classes = [...new Set(promoData.map(r => r.class).filter(Boolean))];
      const courses = [...new Set(promoData.map(r => r.courseName).filter(Boolean))];
      const statuses = [...new Set(promoData.map(r => promoShootStatusStr(r.shootStatus)).filter(Boolean))];
      const monthOpts = CALENDAR_MONTHS_ORDER.filter(m => promoData.some(r => r.month === m));

      const prevStatusNorm = promoShootStatusStr(prev.shootStatus);
      const shootOk =
        prevStatusNorm &&
        statuses.some(s => s.toLowerCase() === prevStatusNorm.toLowerCase());
      const shootVal = shootOk
        ? statuses.find(s => s.toLowerCase() === prevStatusNorm.toLowerCase()) || ''
        : '';

      const next = {
        teacherName: prev.teacherName.filter(t => teachers.includes(t)),
        class: prev.class.filter(c => classes.includes(c)),
        courseName: prev.courseName.filter(c => courses.includes(c)),
        month: prev.month.filter(m => monthOpts.includes(m)),
        shootStatus: shootVal,
        startDate: typeof prev.startDate === 'string' ? prev.startDate : '',
        endDate: typeof prev.endDate === 'string' ? prev.endDate : '',
      };
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
      return next;
    });
  }, [promoData]);

  const uniqueTeachers = useMemo(
    () => Array.from(new Set(promoData.map(r => r.teacherName).filter(Boolean))).sort(),
    [promoData]
  );
  const uniqueClasses = useMemo(
    () => Array.from(new Set(promoData.map(r => r.class).filter(Boolean))).sort(),
    [promoData]
  );
  const uniqueCourseNames = useMemo(
    () => Array.from(new Set(promoData.map(r => r.courseName).filter(Boolean))).sort(),
    [promoData]
  );
  const uniqueMonths = useMemo(
    () => CALENDAR_MONTHS_ORDER.filter(m => promoData.some(r => r.month === m)),
    [promoData]
  );
  const uniqueStatuses = useMemo(
    () => Array.from(new Set(promoData.map(r => promoShootStatusStr(r.shootStatus)).filter(Boolean))).sort(),
    [promoData]
  );

  const filteredData = useMemo(() => {
    let data = [...promoData];
    if (filters.teacherName.length > 0) {
      data = data.filter(r => filters.teacherName.includes(r.teacherName));
    }
    if (filters.class.length > 0) {
      data = data.filter(r => filters.class.includes(r.class));
    }
    if (filters.courseName.length > 0) {
      data = data.filter(r => filters.courseName.includes(r.courseName));
    }
    if (filters.month.length > 0) {
      data = data.filter(r => filters.month.includes(r.month));
    }
    if (filters.shootStatus) {
      const target = filters.shootStatus.toLowerCase();
      data = data.filter(r => promoShootStatusStr(r.shootStatus).toLowerCase() === target);
    }
    if (filters.startDate) {
      data = data.filter(r => {
        const key = comparableScheduleDateKey(r.shootDeadline);
        return key && key >= filters.startDate;
      });
    }
    if (filters.endDate) {
      data = data.filter(r => {
        const key = comparableScheduleDateKey(r.shootDeadline);
        return key && key <= filters.endDate;
      });
    }
    return data;
  }, [promoData, filters]);

  const handleReset = () => {
    setFilters({
      teacherName: [],
      class: [],
      courseName: [],
      month: [],
      shootStatus: '',
      startDate: '',
      endDate: '',
    });
    try {
      localStorage.removeItem(PROMO_FILTERS_CACHE_KEY);
    } catch (_) {}
  };

  const hasActiveFilters =
    filters.teacherName.length > 0 ||
    filters.class.length > 0 ||
    filters.courseName.length > 0 ||
    filters.month.length > 0 ||
    filters.shootStatus ||
    Boolean(filters.startDate) ||
    Boolean(filters.endDate);

  const doneCount = filteredData.filter(
    r => promoShootStatusStr(r.shootStatus).toLowerCase() === 'done'
  ).length;
  const pendingCount = filteredData.filter(
    r => promoShootStatusStr(r.shootStatus).toLowerCase() === 'pending'
  ).length;

  if (promoLoading) {
    return <CartoonLoader message="Loading promotional schedule..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppPageHeader
        onBack={() => navigate('/')}
        title="AFS Promotional Content Schedule"
        rightSlot={
          <button
            type="button"
            onClick={refetchPromo}
            className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        }
      />

      <main className="max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-8">
        {promoError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
                <p className="text-sm text-red-700 mt-1">{promoError}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between mb-5">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200">
                <span className="text-sm text-gray-500">Total</span>
                <span className="text-lg font-bold text-gray-800">{filteredData.length}</span>
              </div>
              <div className="flex items-center gap-2 bg-green-50 rounded-lg px-4 py-2.5 border border-green-200">
                <span className="text-sm text-green-600">Done</span>
                <span className="text-lg font-bold text-green-700">{doneCount}</span>
              </div>
              <div className="flex items-center gap-2 bg-yellow-50 rounded-lg px-4 py-2.5 border border-yellow-200">
                <span className="text-sm text-yellow-600">Pending</span>
                <span className="text-lg font-bold text-yellow-700">{pendingCount}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3 sm:ml-auto sm:justify-end min-w-0">
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                  Shoot deadline from
                </span>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors w-full min-w-[10.5rem] sm:w-40"
                />
              </label>
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                  Shoot deadline to
                </span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors w-full min-w-[10.5rem] sm:w-40"
                />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <MultiSelect
              label="All teachers"
              options={uniqueTeachers}
              value={filters.teacherName}
              onChange={v => setFilters(f => ({ ...f, teacherName: v }))}
            />
            <MultiSelect
              label="All classes"
              options={uniqueClasses}
              value={filters.class}
              onChange={v => setFilters(f => ({ ...f, class: v }))}
            />
            <MultiSelect
              label="All courses"
              options={uniqueCourseNames}
              value={filters.courseName}
              onChange={v => setFilters(f => ({ ...f, courseName: v }))}
            />
            <MultiSelect
              label="All months"
              options={uniqueMonths}
              value={filters.month}
              onChange={v => setFilters(f => ({ ...f, month: v }))}
            />
            <select
              value={filters.shootStatus}
              onChange={e => setFilters(f => ({ ...f, shootStatus: e.target.value }))}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <option value="">All statuses</option>
              {uniqueStatuses.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={handleReset}
                className="text-sm text-gray-500 hover:text-red-600 flex items-center gap-1.5 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filters
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0">
          {filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-base font-medium">No entries found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="w-full min-w-0 overflow-x-auto xl:overflow-x-visible">
              <table className="w-full table-fixed border-collapse divide-y divide-gray-200 min-w-0">
                <colgroup>
                  <col className="w-[11%]" />
                  <col className="w-[7%]" />
                  <col className="w-[13%]" />
                  <col className="w-[13%]" />
                  <col className="w-[18%]" />
                  <col className="w-[24%]" />
                  <col className="w-[9%]" />
                  <col className="w-[5%]" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Shoot deadline</th>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Class</th>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Subject</th>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Teacher</th>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Course name</th>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Topic</th>
                    <th className="px-2 sm:px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Status</th>
                    <th className="px-1 sm:px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider align-top">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredData.map((row, idx) => (
                    <tr
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedRow(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedRow(row);
                        }
                      }}
                      className={`cursor-pointer hover:bg-purple-50/60 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                    >
                      <td className="min-w-0 px-2 sm:px-3 py-2.5 text-sm text-gray-700 align-top break-words">
                        {(() => {
                          const d = formatDate(row.shootDeadlineRaw || row.shootDeadline);
                          return d === 'N/A' ? <span className="text-gray-300">—</span> : d;
                        })()}
                      </td>
                      <td className="min-w-0 px-2 sm:px-3 py-2.5 text-sm text-gray-700 align-top break-words">
                        {row.class || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="min-w-0 px-2 sm:px-3 py-2.5 text-sm text-gray-800 font-medium align-top break-words">
                        {row.subject || <span className="text-gray-300 font-normal">—</span>}
                      </td>
                      <td className="min-w-0 px-2 sm:px-3 py-2.5 text-sm text-gray-700 align-top break-words">
                        {row.teacherName || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="min-w-0 px-2 sm:px-3 py-2.5 text-sm text-gray-600 align-top break-words">
                        {row.courseName || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="min-w-0 px-2 sm:px-3 py-2.5 text-sm text-gray-600 align-top break-all">
                        <div className="line-clamp-3 break-words" title={row.topic}>
                          {row.topic || <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2.5 align-top">
                        <StatusBadge status={row.shootStatus} />
                      </td>
                      <td className="px-1 sm:px-2 py-2.5 text-center align-top whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRow(row);
                          }}
                          className="inline-flex text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded hover:bg-gray-100"
                          title="View details"
                          aria-label="View details"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Showing {filteredData.length} of {promoData.length} entries — click a row for full details
        </p>
      </main>

      {selectedRow && (
        <PromoDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}
